class InputSanitizationService {
  constructor() {
    // Dangerous characters that should be blocked or escaped
    this.dangerousChars = {
      // XSS Prevention
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '&': '&amp;',
      
      // SQL Injection Prevention
      ';': '',
      '--': '',
      '/*': '',
      '*/': '',
      'xp_': '',
      'sp_': '',
      
      // Command Injection Prevention
      '|': '',
      '`': '',
      '$': '',
      
      // Path Traversal Prevention
      '../': '',
      '..\\': '',
      
      // Script Injection Prevention
      'javascript:': '',
      'vbscript:': '',
      'data:': '',
      
      // Additional dangerous patterns
      '%': '',
      '+': '',
      '=': '',
      '!': '',
      '/': ''
    };

    // Patterns for various attack vectors
    this.maliciousPatterns = [
      // XSS patterns - use simple tag-name detection without nested quantifiers
      /<script\b/gi,
      /<\/script>/gi,
      /<iframe\b/gi,
      /<\/iframe>/gi,
      /<object\b/gi,
      /<\/object>/gi,
      /<embed\b/gi,
      /<applet\b/gi,
      /<\/applet>/gi,
      /javascript:/gi,
      /vbscript:/gi,
      /on\w{1,30}\s*=/gi, // Event handlers like onclick, onload, etc.
      
      // SQL Injection patterns
      /\b(?:SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b/gi,
      /--|#|\/\*|\*\//g,
      /\b(?:OR|AND)\b[^\n]{0,50}\b(?:=|LIKE)\b/gi,
      /'[^'\n]{0,50}\b(?:OR|AND)\b[^'\n]{0,50}'/gi,
      
      // Command Injection patterns
      /[|;&`]|\$\(/g,
      /\b(?:cmd|powershell|bash|sh|exec)\b/gi,
      
      // Path traversal patterns
      /\.\.\/|\.\.\\|%2e%2e%2f|%2e%2e%5c/gi,
      
      // Additional malicious patterns
      /eval\s{0,5}\(/gi,
      /expression\s{0,5}\(/gi,
      /setTimeout\s{0,5}\(/gi,
      /setInterval\s{0,5}\(/gi
    ];

    // Allowed characters for different input types
    this.allowedPatterns = {
      alphanumeric: /^[a-zA-Z0-9\s]*$/,
      alphanumericWithBasicPunct: /^[a-zA-Z0-9\s\.\,\-]*$/,
      email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      phone: /^[\d\s\+\-\(\)]*$/,
      name: /^[a-zA-Z\s\.\-']*$/,
      address: /^[a-zA-Z0-9\s\.\,\-\#]*$/,
      url: /^https?:\/\/[a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,}(\/[a-zA-Z0-9\-\._~:\/?#[\]@!$&'()*+,;%=]*)?$/,
      number: /^\d+(\.\d+)?$/,
      date: /^\d{4}-\d{2}-\d{2}$/,
      textarea: /^[a-zA-Z0-9\s\.\,\-\!\?\(\)\[\]\{\}\n\r]*$/
    };
  }

  /**
   * Sanitize input by removing/escaping dangerous characters
   * @param {string} input - The input string to sanitize
   * @param {string} type - The type of input (text, email, phone, etc.)
   * @returns {object} - Sanitized input and validation result
   */
  sanitizeInput(input, type = 'text') {
    if (!input || typeof input !== 'string') {
      return {
        sanitized: '',
        isValid: true,
        errors: []
      };
    }

    const errors = [];
    let sanitized = input.trim();

    // Check for malicious patterns first
    const maliciousFound = this.detectMaliciousPatterns(sanitized);
    if (maliciousFound.length > 0) {
      errors.push(`Potentially malicious content detected: ${maliciousFound.join(', ')}`);
      return {
        sanitized: '',
        isValid: false,
        errors
      };
    }

    // Apply type-specific validation
    const typeValidation = this.validateInputType(sanitized, type);
    if (!typeValidation.isValid) {
      errors.push(...typeValidation.errors);
    }

    // Remove dangerous characters
    sanitized = this.removeDangerousCharacters(sanitized);

    // HTML entity encode remaining special characters
    sanitized = this.htmlEncode(sanitized);

    // Length validation
    if (sanitized.length > 1000) {
      errors.push('Input exceeds maximum length of 1000 characters');
      sanitized = sanitized.substring(0, 1000);
    }

    return {
      sanitized,
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Detect malicious patterns in input
   * @param {string} input - Input to check
   * @returns {array} - Array of detected malicious patterns
   */
  detectMaliciousPatterns(input) {
    const detected = [];
    if (!input || input.length > 10000) return detected;
    
    this.maliciousPatterns.forEach(pattern => {
      if (pattern.test(input)) {
        detected.push(pattern.source);
      }
    });

    return detected;
  }

  /**
   * Validate input based on type
   * @param {string} input - Input to validate
   * @param {string} type - Type of validation to apply
   * @returns {object} - Validation result
   */
  validateInputType(input, type) {
    const errors = [];
    
    if (!this.allowedPatterns[type]) {
      // Default text validation - remove most special characters
      const hasDisallowedChars = /[<>'"&;|`$%+=!\/\\]/.test(input);
      if (hasDisallowedChars) {
        errors.push('Input contains disallowed special characters');
      }
    } else {
      if (!this.allowedPatterns[type].test(input)) {
        errors.push(`Input format is invalid for type: ${type}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Remove dangerous characters from input
   * @param {string} input - Input to clean
   * @returns {string} - Cleaned input
   */
  removeDangerousCharacters(input) {
    let cleaned = input;

    // Remove dangerous character patterns
    Object.keys(this.dangerousChars).forEach(char => {
      const replacement = this.dangerousChars[char];
      cleaned = cleaned.replace(new RegExp(this.escapeRegExp(char), 'gi'), replacement);
    });

    return cleaned;
  }

  /**
   * HTML encode special characters
   * @param {string} input - Input to encode
   * @returns {string} - HTML encoded input
   */
  htmlEncode(input) {
    const htmlEntities = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '&': '&amp;'
    };

    return input.replace(/[<>"'&]/g, match => htmlEntities[match] || match);
  }

  /**
   * Escape regular expression special characters
   * @param {string} string - String to escape
   * @returns {string} - Escaped string
   */
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Sanitize form data object
   * @param {object} formData - Form data to sanitize
   * @param {object} fieldTypes - Field type mappings
   * @returns {object} - Sanitized form data and validation results
   */
  sanitizeFormData(formData, fieldTypes = {}) {
    const sanitized = {};
    const errors = {};
    let isValid = true;

    Object.keys(formData).forEach(field => {
      const fieldType = fieldTypes[field] || 'text';
      const result = this.sanitizeInput(formData[field], fieldType);
      
      sanitized[field] = result.sanitized;
      
      if (!result.isValid) {
        errors[field] = result.errors;
        isValid = false;
      }
    });

    return {
      sanitized,
      isValid,
      errors
    };
  }

  /**
   * Create input validation for React components
   * @param {string} type - Input type
   * @returns {function} - Validation function
   */
  createValidator(type = 'text') {
    return (value) => {
      const result = this.sanitizeInput(value, type);
      return {
        value: result.sanitized,
        isValid: result.isValid,
        errors: result.errors
      };
    };
  }

  /**
   * Real-time input sanitization for form fields
   * @param {string} value - Current input value
   * @param {string} type - Input type
   * @returns {string} - Sanitized value safe for display
   */
  realTimeSanitize(value, type = 'text') {
    if (!value || typeof value !== 'string') return '';

    // Remove obviously malicious patterns immediately
    let sanitized = value;
    
    // Remove script tags and similar immediately
    sanitized = sanitized.replace(/<script\b/gi, '');
    sanitized = sanitized.replace(/<\/script>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    
    // Remove SQL injection patterns
    sanitized = sanitized.replace(/--|\/\*|\*\//g, '');
    
    // Remove command injection characters
    sanitized = sanitized.replace(/[\|`$]/g, '');

    // Type-specific filtering
    switch (type) {
      case 'alphanumeric':
        sanitized = sanitized.replace(/[^a-zA-Z0-9\s]/g, '');
        break;
      case 'email':
        sanitized = sanitized.replace(/[^a-zA-Z0-9@\.\-_]/g, '');
        break;
      case 'phone':
        sanitized = sanitized.replace(/[^0-9\s\+\-\(\)]/g, '');
        break;
      case 'number':
        sanitized = sanitized.replace(/[^0-9\.]/g, '');
        break;
      case 'name':
        sanitized = sanitized.replace(/[^a-zA-Z\s\.\-']/g, '');
        break;
      default:
        // Default: remove most dangerous characters
        sanitized = sanitized.replace(/[<>'"&;|`$%+=!\/\\]/g, '');
    }

    return sanitized;
  }

  /**
   * Check if input is safe (no dangerous patterns)
   * @param {string} input - Input to check
   * @returns {boolean} - True if safe
   */
  isSafeInput(input) {
    if (!input || typeof input !== 'string') return true;

    // Allow single character inputs (including Shift, Ctrl, etc.)
    if (input.length === 1) {
      // Allow all printable characters and common keys
      const charCode = input.charCodeAt(0);
      
      // Allow printable ASCII characters (32-126)
      if (charCode >= 32 && charCode <= 126) {
        // Block only extremely dangerous single characters
        const dangerousSingleChars = ['<', '>', '&', '"', "'", '`'];
        return !dangerousSingleChars.includes(input);
      }
      
      // Allow control characters and special keys
      return true;
    }

    const maliciousFound = this.detectMaliciousPatterns(input);
    return maliciousFound.length === 0;
  }

  /**
   * Show validation error message (disabled - silently block invalid input)
   * @param {array} errors - Array of error messages
   */
  showValidationError(errors) {
    // No-op: silently block invalid input without showing popup
  }
}

export default new InputSanitizationService();